import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { inked, toon } from "../toon";
import { NECK_BASE, type SkaterLook } from "./look";
import { cloth } from "./materials";
import { facets } from "./geom";

// Outfit extras on the torso and hips: patches, shirt and suit details, bandana, belt.
// These are the capsule torso's; the shaped torso's are in `outfit-natural.ts`
// (`TORSOS` in `torso.ts` picks).

/** A round embroidered patch: a colored disc with a white border, facing +x of whatever it's added to. */
export function buildPatch(color: string, radius: number, x: number): THREE.Mesh {
  const patch = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, 0.012, facets(16)), toon(color, { roughness: 0.7 }));
  patch.name = "patch";
  patch.rotation.z = Math.PI / 2;
  patch.position.set(-x, -0.06, 0);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.006, facets(4, 2), facets(16)), toon("#ffffff", { roughness: 0.7 }));
  rim.rotation.x = Math.PI / 2;
  patch.add(rim);
  return patch;
}

/** Western shirt and space suit details on the capsule's chest (torso space, chest toward -x). */
export function buildTopDetails(look: SkaterLook, torso: THREE.Group, width: number): void {
  const { style, trim } = look.top;
  const front = -0.135 * width;
  if (style === "western") {
    const trimMat = cloth(trim ?? "#f1e3c2");
    for (const side of [-1, 1]) {
      const flap = inked(new RoundedBoxGeometry(0.02, 0.04, 0.09, facets(2, 1), 0.008), trimMat, 0.008);
      flap.name = "pocket";
      flap.position.set(front - 0.01, 0.32, side * 0.08);
      torso.add(flap);
    }
    const pearl = toon("#fbf7ee", { roughness: 0.15, metalness: 0.2 });
    for (let i = 0; i < 4; i++) {
      const snap = new THREE.Mesh(new THREE.SphereGeometry(0.013, facets(8), facets(6, 2)), pearl);
      snap.name = "snap";
      snap.position.set(front - 0.012, 0.1 + i * 0.08, 0);
      torso.add(snap);
    }
  } else if (style === "spacesuit") {
    const pack = inked(new RoundedBoxGeometry(0.04, 0.1, 0.14, facets(2, 1), 0.015), toon("#9aa3b5", { roughness: 0.4, metalness: 0.3 }), 0.01);
    pack.name = "chest-pack";
    pack.position.set(front - 0.015, 0.2, 0);
    torso.add(pack);
    const lights = ["#ef476f", "#06d6a0", "#ffd166"];
    lights.forEach((c, i) => {
      const light = new THREE.Mesh(new THREE.SphereGeometry(0.012, facets(8), facets(6, 2)), new THREE.MeshBasicMaterial({ color: c }));
      light.position.set(front - 0.037, 0.22, -0.04 + i * 0.04);
      torso.add(light);
    });
    const patch = buildPatch(trim ?? "#1d4ed8", 0.04, -front + 0.004);
    patch.position.y = 0.34;
    patch.position.z = 0.07;
    torso.add(patch);
  }
}

/**
 * A bandana knotted at the neck, its point hanging over the chest (torso
 * space).
 */
export function buildBandana(color: string, torso: THREE.Group): void {
  // Tied round the neck (#80), the point hanging down the chest: a flat
  // triangle, apex down.
  const band = inked(new THREE.TorusGeometry(0.064, 0.02, facets(8, 2), facets(20)), cloth(color), 0.008);
  band.name = "bandana-band";
  band.rotation.x = Math.PI / 2;
  band.position.y = NECK_BASE + 0.06;
  torso.add(band);
  const pointGeo = new THREE.ConeGeometry(0.11, 0.17, facets(3));
  pointGeo.rotateZ(Math.PI);
  pointGeo.rotateY(Math.PI / 2);
  pointGeo.scale(0.25, 1, 1);
  const point = inked(pointGeo, cloth(color), 0.01);
  point.name = "bandana";
  point.position.set(-0.14, 0.37, 0);
  point.rotation.z = -0.2;
  torso.add(point);
}

/** A belt round the waist of the toy pants with a big buckle out front (hips space, front -x). */
export function buildBelt(bottoms: SkaterLook["bottoms"], hips: THREE.Group): void {
  const belt = inked(new RoundedBoxGeometry(0.21, 0.035, 0.31, facets(2, 1), 0.015), toon("#3a2418", { roughness: 0.6 }, "leather"), 0.008);
  belt.name = "belt";
  belt.position.y = 0.06;
  hips.add(belt);
  const buckle = buildBuckle(bottoms);
  buckle.position.set(-0.11, 0.06, 0);
  hips.add(buckle);
}

/** A belt's big buckle, facing -x, for the caller to place. */
export function buildBuckle(bottoms: SkaterLook["bottoms"]): THREE.Mesh {
  const buckle = inked(new RoundedBoxGeometry(0.02, 0.065, 0.1, facets(2, 1), 0.01), toon(bottoms.buckle!, { roughness: 0.2, metalness: 0.85 }, "metal"), 0.008);
  buckle.name = "buckle";
  return buckle;
}
